import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./editStudentsData";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faSearch,
  faFilter,
  faUsers,
  faUserGraduate,
  faIdCard,
  faSort,
  faSpinner,
  faDatabase,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";

const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

const FetchStudents = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  const [filters, setFilters] = useState({
    useStudentId: false,
    useStudentName: false,
    useStudentClass: false,
    student_id: "",
    student_name: "",
    student_class: "",
  });

  const [students, setStudents] = useState([]);
  const [studentIdSuggestions, setStudentIdSuggestions] = useState([]);
  const [studentNameSuggestions, setStudentNameSuggestions] = useState([]);
  const [backendError, setBackendError] = useState(null);

  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  // Calculate active filters
  useEffect(() => {
    const activeCount = [
      filters.useStudentId && filters.student_id,
      filters.useStudentName && filters.student_name,
      filters.useStudentClass && filters.student_class,
    ].filter(Boolean).length;
    setActiveFilters(activeCount);
  }, [filters]);

  // Test PHP backend connection
  const testPhpBackend = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/studentCrudOP.php?action=fetch-students&limit=1`
      );
      return response.data.success === true;
    } catch (error) {
      console.error("PHP Backend test failed:", error);
      return false;
    }
  };

  // Debounced suggestion fetching
  const fetchStudentIdSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length >= 2) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/studentCrudOP.php?action=search-suggestions&type=student-id&query=${query}`
          );
          setStudentIdSuggestions(response.data.suggestions || []);
        } catch (error) {
          console.error("Error fetching student ID suggestions:", error);
          setStudentIdSuggestions([]);
        }
      } else {
        setStudentIdSuggestions([]);
      }
    }, 300),
    []
  );

  const fetchStudentNameSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length >= 2) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/studentCrudOP.php?action=search-suggestions&type=student-name&query=${query}`
          );
          setStudentNameSuggestions(response.data.suggestions || []);
        } catch (error) {
          console.error("Error fetching student name suggestions:", error);
          setStudentNameSuggestions([]);
        }
      } else {
        setStudentNameSuggestions([]);
      }
    }, 300),
    []
  );

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });

    if (name === "student_id" && filters.useStudentId) {
      fetchStudentIdSuggestions(value);
    }
    if (name === "student_name" && filters.useStudentName) {
      fetchStudentNameSuggestions(value);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFilters({ ...filters, [name]: checked });

    // Clear suggestions when filter is disabled
    if (name === "useStudentId" && !checked) {
      setStudentIdSuggestions([]);
    }
    if (name === "useStudentName" && !checked) {
      setStudentNameSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion, type) => {
    if (type === "student_id") {
      setFilters({ ...filters, [type]: suggestion.student_id || suggestion });
    } else if (type === "student_name") {
      setFilters({ ...filters, [type]: suggestion.name || suggestion });
    }

    if (type === "student_id") setStudentIdSuggestions([]);
    if (type === "student_name") setStudentNameSuggestions([]);
  };

  // Fetch students based on filters
  const fetchStudents = async () => {
    setIsLoading(true);
    setBackendError(null);

    try {
      // First test if PHP backend is available
      const phpAvailable = await testPhpBackend();

      if (!phpAvailable) {
        throw new Error(
          "PHP backend is not accessible. Please ensure the server is running."
        );
      }

      // Build query parameters
      const params = new URLSearchParams({
        action: "fetch-students",
        limit: 100, // Get more students for better UX
      });

      // Add filters only if they're checked
      if (filters.useStudentId && filters.student_id) {
        params.append("student_id", filters.student_id);
      }
      if (filters.useStudentName && filters.student_name) {
        params.append("student_name", filters.student_name);
      }
      if (filters.useStudentClass && filters.student_class) {
        params.append("student_class", filters.student_class);
      }

      const url = `${API_BASE_URL}/studentCrudOP.php?${params.toString()}`;
      console.log("Fetching from:", url);

      const response = await axios.get(url, {
        timeout: 10000, // 10 second timeout
      });

      console.log("PHP Response:", response.data);

      if (response.data && response.data.success) {
        setStudents(response.data.data || []);
      } else {
        throw new Error(
          response.data?.error || "No student data returned from server"
        );
      }
    } catch (error) {
      console.error("Error fetching students data:", error);

      let errorMessage = "Failed to fetch students. Please try again.";

      if (error.code === "ECONNABORTED") {
        errorMessage =
          "Request timeout. The server is taking too long to respond.";
      } else if (error.code === "ERR_NETWORK") {
        errorMessage = "Network error. Cannot connect to the PHP backend.";
      } else if (error.response?.status === 404) {
        errorMessage = "PHP backend not found. Check if the file exists.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setBackendError(errorMessage);
      setStudents([]);

      Swal.fire({
        icon: "error",
        title: "Connection Error",
        html: `
          <div style="text-align: left;">
            <p><strong>${errorMessage}</strong></p>
            <p>Please check:</p>
            <ul>
              <li>PHP server is running</li>
              <li>File exists at: ${API_BASE_URL}/studentCrudOP.php</li>
              <li>No errors in PHP file</li>
              <li>Check browser console for more details</li>
            </ul>
            <p><small>Test URL: ${API_BASE_URL}/studentCrudOP.php?action=fetch-students</small></p>
          </div>
        `,
        background: "#f8fafc",
        color: "#2d3748",
        confirmButtonColor: "#667eea",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Edit action
  const handleEdit = (student) => {
    navigate("/Edit-Student-Panel", { state: { student } });
  };

  // Handle Delete action
  const handleDelete = async (studentId, studentName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete student "${studentName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f56565",
      cancelButtonColor: "#cbd5e0",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      background: "#f8fafc",
      color: "#2d3748",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          // Use FormData for POST request
          const formData = new FormData();
          formData.append("action", "delete-student");
          formData.append("student_id", studentId);

          const response = await axios.post(
            `${API_BASE_URL}/studentCrudOP.php`,
            formData
          );

          if (!response.data.success) {
            throw new Error(response.data.error || "Failed to delete student");
          }

          return { success: true };
        } catch (error) {
          let errorMessage = "Failed to delete student";
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          }
          throw new Error(errorMessage);
        }
      },
    });

    if (result.isConfirmed) {
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "The student has been deleted successfully.",
        confirmButtonColor: "#667eea",
        background: "#f8fafc",
        color: "#2d3748",
      });
      // Refresh the student list
      fetchStudents();
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      useStudentId: false,
      useStudentName: false,
      useStudentClass: false,
      student_id: "",
      student_name: "",
      student_class: "",
    });
    setStudentIdSuggestions([]);
    setStudentNameSuggestions([]);
    setBackendError(null);
  };

  // Load all students (no filters)
  const handleLoadAllStudents = () => {
    handleResetFilters();
    // Use setTimeout to ensure state is updated before fetch
    setTimeout(() => fetchStudents(), 100);
  };

  // Retry fetching with PHP backend
  const retryWithPHP = () => {
    fetchStudents();
  };

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="fetch-students-container">
        {/* Header Section */}
        <div className="dashboard-header">
          <h2>
            <FontAwesomeIcon icon={faUserGraduate} />
            Student Management
          </h2>
          <div className="header-actions">
            {backendError && (
              <button
                onClick={retryWithPHP}
                className="retry-button"
                title="Retry PHP backend"
              >
                <FontAwesomeIcon icon={faSync} spin={isLoading} />
                Retry PHP Backend
              </button>
            )}
            <button
              onClick={fetchStudents}
              className="search-button"
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faSearch} />
              {isLoading ? "Searching..." : "Search Students"}
            </button>
          </div>
        </div>

        {/* Backend Error Alert */}
        {backendError && (
          <div className="backend-error-alert">
            <p>
              <strong>Backend Error:</strong> {backendError}
            </p>
            <button onClick={retryWithPHP} className="retry-btn-small">
              <FontAwesomeIcon icon={faSync} />
              Retry
            </button>
          </div>
        )}

        {/* Filters Card */}
        <div className="filters-card">
          <div className="filters-header">
            <h3>
              <FontAwesomeIcon icon={faFilter} />
              Search Filters
              <span className="stats-badge">{activeFilters} active</span>
            </h3>
          </div>

          <div className="filters-grid">
            {/* Student ID Filter */}
            <div className="filter-group">
              <label className="filter-label">
                <span className="checkbox-container">
                  <input
                    type="checkbox"
                    name="useStudentId"
                    checked={filters.useStudentId}
                    onChange={handleCheckboxChange}
                    className="custom-checkbox"
                    disabled={isLoading}
                  />
                </span>
                <FontAwesomeIcon icon={faIdCard} />
                Student ID
              </label>
              <input
                type="text"
                name="student_id"
                value={filters.student_id}
                onChange={handleInputChange}
                placeholder="Enter student ID"
                disabled={!filters.useStudentId || isLoading}
                className="filter-input"
              />
              {studentIdSuggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {studentIdSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() =>
                        handleSuggestionClick(suggestion, "student_id")
                      }
                    >
                      {suggestion.student_id || suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Student Name Filter */}
            <div className="filter-group">
              <label className="filter-label">
                <span className="checkbox-container">
                  <input
                    type="checkbox"
                    name="useStudentName"
                    checked={filters.useStudentName}
                    onChange={handleCheckboxChange}
                    className="custom-checkbox"
                    disabled={isLoading}
                  />
                </span>
                <FontAwesomeIcon icon={faUsers} />
                Student Name
              </label>
              <input
                type="text"
                name="student_name"
                value={filters.student_name}
                onChange={handleInputChange}
                placeholder="Enter student name"
                disabled={!filters.useStudentName || isLoading}
                className="filter-input"
              />
              {studentNameSuggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {studentNameSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() =>
                        handleSuggestionClick(suggestion, "student_name")
                      }
                    >
                      {suggestion.name || suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Student Class Filter */}
            <div className="filter-group">
              <label className="filter-label">
                <span className="checkbox-container">
                  <input
                    type="checkbox"
                    name="useStudentClass"
                    checked={filters.useStudentClass}
                    onChange={handleCheckboxChange}
                    className="custom-checkbox"
                    disabled={isLoading}
                  />
                </span>
                <FontAwesomeIcon icon={faSort} />
                Student Class
              </label>
              <select
                name="student_class"
                value={filters.student_class}
                onChange={handleInputChange}
                disabled={!filters.useStudentClass || isLoading}
                className="filter-select"
              >
                <option value="">Select Class</option>
                {classes.map((cls, index) => (
                  <option key={index} value={cls}>
                    {cls}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="filter-actions">
            <button
              onClick={handleResetFilters}
              className="action-button reset-btn"
              disabled={isLoading}
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Results Section */}
        {isLoading ? (
          <div className="loader">
            <div className="loader-spinner"></div>
            <p>Loading students from PHP backend...</p>
          </div>
        ) : students.length > 0 ? (
          <div className="table-container">
            <div className="table-header">
              <h3>Students List</h3>
              <span className="stats-badge">
                {students.length} students found
              </span>
            </div>

            <div className="table-wrapper">
              <table className="student-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Student Name</th>
                    <th>Father Name</th>
                    <th>Mobile</th>
                    <th>Class</th>
                    <th>Section</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <span className="student-id">{student.student_id}</span>
                      </td>
                      <td>
                        <div className="student-info">
                          <strong>{student.name}</strong>
                        </div>
                      </td>
                      <td>{student.father_name}</td>
                      <td>{student.mobile}</td>
                      <td>
                        <span className="status-badge status-active">
                          {student.class}
                        </span>
                      </td>
                      <td>{student.section}</td>
                      <td>
                        <span className="status-badge status-active">
                          {student.family_status}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEdit(student)}
                            className="action-btn edit-btn"
                            title="Edit Student"
                          >
                            <FontAwesomeIcon icon={faEdit} color="#4299e1" />
                          </button>
                          <button
                            onClick={() =>
                              handleDelete(student.student_id, student.name)
                            }
                            className="action-btn delete-btn"
                            title="Delete Student"
                          >
                            <FontAwesomeIcon icon={faTrash} color="#f56565" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <div className="empty-state">
              <FontAwesomeIcon icon={faDatabase} className="empty-state-icon" />
              <h3>No Students Found</h3>
              <p>
                {backendError
                  ? "PHP backend connection failed"
                  : "Use the filters above to search for students"}
              </p>
              <div className="empty-state-actions">
                <button
                  onClick={handleLoadAllStudents}
                  className="search-button"
                >
                  <FontAwesomeIcon icon={faSearch} />
                  Load All Students
                </button>
                {backendError && (
                  <button onClick={retryWithPHP} className="retry-button">
                    <FontAwesomeIcon icon={faSync} />
                    Retry PHP Backend
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FetchStudents;
